export interface ScriptLocale {
  dir: 'ltr' | 'rtl' | 'auto';
  fontFamily: string;
}

// Short codes used throughout the app for RTL scripts (Sarvam/translation
// language lists, videoTranslationConfig.ts's targetLanguages, etc.).
const RTL_LANGUAGES = new Set(['ar', 'ur', 'he', 'fa']);

// Mirrors the script-aware rendering already used server-side for burned-in
// video text (see backend audioToVideoService.ts / clipGenerationService.ts
// SCRIPT_RANGES) — same Unicode ranges, so a given piece of text gets a
// consistent script "verdict" whether it's being edited in the browser or
// rendered into a video. Extended here with Cyrillic/CJK/Sinhala, which
// matter for in-browser editing even though the backend's caption-burning
// font set doesn't need to cover them yet.
const SCRIPT_RANGES: Array<{ fontFamily: string; pattern: RegExp }> = [
  { fontFamily: 'Noto Sans Tamil', pattern: /[஀-௿]/ },
  { fontFamily: 'Noto Sans Devanagari', pattern: /[ऀ-ॿ]/ }, // Hindi, Marathi, Nepali, Sanskrit
  { fontFamily: 'Noto Sans Telugu', pattern: /[ఀ-౿]/ },
  { fontFamily: 'Noto Sans Kannada', pattern: /[ಀ-೿]/ },
  { fontFamily: 'Noto Sans Malayalam', pattern: /[ഀ-ൿ]/ },
  { fontFamily: 'Noto Sans Bengali', pattern: /[ঀ-৿]/ }, // also Assamese
  { fontFamily: 'Noto Sans Gujarati', pattern: /[઀-૿]/ },
  { fontFamily: 'Noto Sans Gurmukhi', pattern: /[਀-੿]/ }, // Punjabi
  { fontFamily: 'Noto Sans Oriya', pattern: /[଀-୿]/ },
  { fontFamily: 'Noto Sans Sinhala', pattern: /[඀-෿]/ },
  { fontFamily: 'Noto Sans Arabic', pattern: /[؀-ۿݐ-ݿ]/ }, // Arabic, Urdu
  { fontFamily: 'Noto Sans', pattern: /[Ѐ-ӿ]/ }, // Cyrillic — Russian
  { fontFamily: "'Noto Sans JP'", pattern: /[぀-ヿｦ-ﾟ]/ }, // Japanese kana
  { fontFamily: "'Noto Sans KR'", pattern: /[가-힣]/ }, // Korean Hangul
  { fontFamily: "'Noto Sans SC'", pattern: /[一-鿿]/ }, // CJK unified ideographs (zh, and ja kanji)
];

// Short language code -> preferred font family, for the common case where
// the field already knows its language (selected from a dropdown, or
// carried on the job/article) rather than needing to guess from content.
const LANGUAGE_FONT: Record<string, string> = {
  ta: 'Noto Sans Tamil',
  hi: 'Noto Sans Devanagari',
  mr: 'Noto Sans Devanagari',
  ne: 'Noto Sans Devanagari',
  te: 'Noto Sans Telugu',
  kn: 'Noto Sans Kannada',
  ml: 'Noto Sans Malayalam',
  bn: 'Noto Sans Bengali',
  as: 'Noto Sans Bengali',
  gu: 'Noto Sans Gujarati',
  pa: 'Noto Sans Gurmukhi',
  or: 'Noto Sans Oriya',
  si: 'Noto Sans Sinhala',
  ar: 'Noto Sans Arabic',
  ur: 'Noto Sans Arabic',
  ru: 'Noto Sans',
  ja: "'Noto Sans JP'",
  ko: "'Noto Sans KR'",
  zh: "'Noto Sans SC'",
};

// Ends in a generic fallback so rendering is still correct (via the
// browser/OS's own per-character font substitution) even when none of the
// named webfonts are actually installed — the named family is a preference,
// not a requirement for correctness.
const FALLBACK_FONT_STACK = '"Noto Sans", -apple-system, "Segoe UI", sans-serif';

function buildFontStack(primary?: string): string {
  return primary ? `${primary}, ${FALLBACK_FONT_STACK}` : FALLBACK_FONT_STACK;
}

/** Scans live text for the first script it recognizes — used when no language code is known for the field. */
export function detectFontFamilyFromText(text: string): string | undefined {
  for (const { fontFamily, pattern } of SCRIPT_RANGES) {
    if (pattern.test(text)) return fontFamily;
  }
  return undefined;
}

/**
 * Resolves the direction + font a text field should use. Prefers an
 * explicit language code (deterministic — no guessing); falls back to
 * scanning the live text when the field's language isn't known (free-text
 * paste fields, generic notes, etc.).
 *
 * Direction is 'auto' rather than a guessed 'ltr'/'rtl' whenever the
 * language is unknown and the text doesn't already contain Arabic-script
 * characters — `dir="auto"` lets the browser's own bidi algorithm decide
 * live from whatever the user types next, which handles mixed-direction
 * text far better than a one-shot regex ever could. We only force 'rtl'
 * ourselves when we're confident (a known RTL language code, or Arabic-
 * script text already present) because dir="auto" only looks at the first
 * strong-direction character, which a short line of leading Latin
 * punctuation could otherwise throw off.
 */
export function resolveTextLocale(languageCode?: string, text?: string): ScriptLocale {
  const namedFont = languageCode ? LANGUAGE_FONT[languageCode] : undefined;
  const detectedFont = namedFont ? undefined : detectFontFamilyFromText(text || '');
  const resolvedFont = namedFont || detectedFont;

  let dir: ScriptLocale['dir'];
  if (languageCode) {
    dir = RTL_LANGUAGES.has(languageCode) ? 'rtl' : 'ltr';
  } else {
    dir = resolvedFont === 'Noto Sans Arabic' ? 'rtl' : 'auto';
  }

  return { dir, fontFamily: buildFontStack(resolvedFont) };
}
